import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import Footer from "../Footer";
import ExportWebsiteModal from "../../modals/ExportWebsiteModal";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import WebTable from "./WebTable";
import _ from "lodash";
import UserContext from "../../utils/Contexts/UserContext";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import ImportModal from "../../modals/ImportModal";
import TablePagination from "../Common/Pagination";
import WebFilterModal from "../../modals/WebFilterModal";
import FilterIcon from "../svg/FilterIcon";
import ThemeContext from "../../utils/Contexts/ThemeContext";

import "./style.css"

function YourWebsites() {
  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const { selectedTheme } = useContext(ThemeContext);
  const [columns, setColumns] = useState({
    domain: true,
    status: true,
    affiliation: true,
    money_back: true,
    link_replacement: true,
    words: true,
    checks: true,
    offers: true,
  });
  const allStatuses = [
    { value: null, name: t("All Statuses") },
    { value: 1, name: t("Declined") },
    { value: 0, name: t("Pending Review") },
    { value: 2, name: t("Approved") },
  ];
  const anyAffiliation = [
    { value: null, name: t("Any Affiliation") },
    { value: 1, name: t("Owner") },
    { value: 2, name: t("Reseller") },
    { value: 0, name: t("Not Verified") },
  ];
  const [activeStatusTab, setActiveStatusTab] = useState(null);
  const [activeAffiliationTab, setActiveAffiliationTab] = useState(null);

  const handleStatusTabClick = (relValue) => {
    setActiveStatusTab(relValue);
    updateFilter("DOMAIN_STATUS", relValue);
  };

  const handleAffiliationTabClick = (relValue) => {
    setActiveAffiliationTab(relValue);
    updateFilter("AFFILIATION", relValue);
  };

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: null,
    2: null,
    3: null,
    4: null,
  });
  const [tableData, setTableData] = useState(null);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      { data: "domain" },
      { data: "domain_status" },
      { data: "affiliation" },
      { data: "money_back_policy" },
      { data: "link_replacement_policy" },
    ],
    order: [],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [],
    },
  });

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const fetchWebsites = () => {
    const payload = _.cloneDeep(formData);
    const url = `sellers/${userData?.uuid}/websites/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  useEffect(() => {
    const payload = { ...formData };
    const order = [];
    Object.keys(columnSorts).forEach((columnIndex) => {
      if (columnSorts[columnIndex]) {
        order.push({
          column: columnIndex,
          dir: columnSorts[columnIndex],
        });
      }
    });
    if (order.length) {
      payload.order = order;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      setTableData(null);
      fetchWebsites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateFilter = (key, value) => {
    setTableData(null);
    setFormData((prevData) => {
      const updatedFilters =
        value === null
          ? prevData.search.filters.filter((filter) => filter.key !== key)
          : [
              ...prevData.search.filters.filter((filter) => filter.key !== key),
              { key: key, value: value },
            ];

      return {
        ...prevData,
        length: 15,
        search: {
          ...prevData.search,
          filters: updatedFilters,
        },
      };
    });
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  const [pendingFilters, setPendingFilters] = useState([]);
  const updatePendingFilter = (key, value) => {
    setPendingFilters((prevFilters) => {
      const updatedFilters = prevFilters.map((filter) =>
        filter.key === key ? { key, value } : filter
      );

      const finalFilters = updatedFilters.some((filter) => filter.key === key)
        ? updatedFilters
        : [...updatedFilters, { key, value }];

      return finalFilters;
    });
  };

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div
          className="page-header pb-0 mb-4"
          style={{ borderBottom: "unset" }}
        >
          <div className="row">
            <div className="col-md d-md-flex justify-content-between align-items-center mb-sm-2 mb-2 mb-md-0">
              <div>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb breadcrumb-no-gutter">
                    <li className="breadcrumb-item">
                      {/* eslint-disable-next-line */}
                      <a href="javascript:;"
                        className="breadcrumb-link"
                        style={{ fontSize: "18px", fontWeight: "400" }}
                      >
                        {t("Websites")}
                      </a>
                    </li>
                  </ol>
                </nav>
                <h1 className="page-header-title">{t("Your Websites")}</h1>
              </div>
              <div className="d-flex gap-3 mt-5 mt-md-0">
                <button
                  className="btn btn-outline-primary text-center btn-export d-flex justify-content-center align-items-center"
                  data-bs-toggle="modal"
                  data-bs-target="#exportModal"
                >
                  {t("Export")}
                  <i
                    className="bi-upload me-1"
                    style={{ paddingLeft: "1em" }}
                  ></i>
                </button>
                <button
                  className="btn btn-outline-primary text-center btn-import d-flex justify-content-center align-items-center"
                  data-bs-toggle="modal"
                  data-bs-target="#importModal"
                >
                  {t("Import")}
                  <i
                    className="bi-download me-1"
                    style={{ paddingLeft: "1em" }}
                  ></i>
                </button>
                <button className="btn btn-primary text-center btn-addWeb d-flex justify-content-between align-items-center">
                  {t("Add Your Website")} <i className="bi-plus-lg ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          className="mb-4"
          style={{
            borderBottom: `.0625rem solid ${
              selectedTheme === "dark" ? "#2f3235" : "rgba(231,234,243,.7)"
            }`,
            marginLeft: "-30px",
            marginRight: "-30px",
          }}
        ></div>
        <div className="card">
          <div className="card-header justify-content-sm-between d-flex flex-row gap-2">
            <div className="d-flex gap-3 w-100">
              <div className="search-input-web">
                <div
                  className="input-group input-group-merge input-group-flush border rounded-2 pe-2"
                  style={{ height: "48px" }}
                >
                  <div className="input-group-prepend input-group-text px-2">
                    <i className="bi-search"></i>
                  </div>
                  <input
                    id="datatableSearch"
                    type="search"
                    className="form-control custom-placeholder"
                    placeholder={t("Search Websites")}
                    aria-label="Search Websites"
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-xxl-flex flex-wrap justify-content-between d-none gap-3">
                <ul
                  className="nav nav-segment d-none d-xxxl-flex justify-content-between"
                  style={{ minWidth: "463px", height: "48px" }}
                  role="tablist"
                >
                  {allStatuses.map((status) => {
                    return (
                      <li className="nav-item" key={status.value}>
                        <a
                          className={`js-loading-state-invoker nav-link ${
                            activeStatusTab === status.value ? "active" : ""
                          }`}
                          id="nav-one-eg1-tab"
                          data-bs-toggle="pill"
                          href="#nav-one-eg1"
                          role="tab"
                          aria-controls="nav-one-eg1"
                          aria-selected={activeStatusTab === status.value}
                          onClick={(e) => {
                            e.preventDefault();
                            handleStatusTabClick(status.value);
                          }}
                          data-hs-loading-state-options='{
                            "targetEl": "#nav-one-eg1",
                            "removeLoaderDelay": "300"
                          }'
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          {status.name}
                        </a>
                      </li>
                    );
                  })}
                </ul>
                <ul
                  className="nav nav-segment justify-content-between"
                  style={{ minWidth: "426px", height: "48px" }}
                  role="tablist"
                >
                  {anyAffiliation.map((aff) => {
                    return (
                      <li className="nav-item" key={aff.value}>
                        <a
                          className={`js-loading-state-invoker nav-link ${
                            activeAffiliationTab === aff.value ? "active" : ""
                          }`}
                          id="nav-one-eg1-tab"
                          data-bs-toggle="pill"
                          href="#nav-one-eg1"
                          role="tab"
                          aria-controls="nav-one-eg1"
                          aria-selected={activeAffiliationTab === aff.value}
                          onClick={(e) => {
                            e.preventDefault();
                            handleAffiliationTabClick(aff.value);
                          }}
                          data-hs-loading-state-options='{
                            "targetEl": "#nav-one-eg1",
                            "removeLoaderDelay": "300"
                          }'
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          {aff.name}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="d-grid d-flex justify-content-end align-items-center gap-2">
              <div className="d-xxxl-none d-block">
                <div className="btn-group dropupend ms-auto">
                  <button
                    className="btn btn-white text-nowrap"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#offcanvasWebFilter"
                    aria-controls="offcanvasWebFilter"
                  >
                    <FilterIcon />{" "}
                    <span className="ps-1 align-middle filter-btn">
                      {t("Filters")}
                      {pendingFilters?.length > 0 &&
                        `(${pendingFilters?.length})`}
                    </span>{" "}
                  </button>
                </div>
              </div>
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-white w-100 text-nowrap filter-btn"
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                >
                  <i className="bi bi-layout-three-columns me-1"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <WebTable
            data={tableData}
            setData={setTableData}
            fetchData={fetchWebsites}
            columnsVisibility={columns}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
          />

          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>
      <ImportModal fetchImportsData={fetchWebsites} flag={true} />
      <ExportWebsiteModal />
      <Footer />
      <WebFilterModal
        allStatuses={allStatuses}
        anyAffiliation={anyAffiliation}
        activeStatusTab={activeStatusTab}
        activeAffiliationTab={activeAffiliationTab}
        setFormData={setFormData}
        setActiveAffiliationTab={setActiveAffiliationTab}
        setActiveStatusTab={setActiveStatusTab}
        updatePendingFilter={updatePendingFilter}
        pendingFilters={pendingFilters}
        setPendingFilters={setPendingFilters}
      />
    </main>
  );
}

export default YourWebsites;
